import _ from 'lodash';

import { PurchaseOrderItemTypes } from 'services/purchaseOrders';
import { roundToDecimals, toMulticurrencyCalculate } from 'helpers';
import { SalesOrderItemTypes } from 'services/salesOrders';
import { Uom } from 'services/uoms';

import {
  DiscountTypes,
  Discount,
  SalesOrderBundleItem,
  CalculatedTotals,
  SalesOrder,
  SalesOrderStatus,
} from './types';
import { ItemUomConversion } from 'services/items';

export const calculateTotals = (
  price: number,
  quantity: number,
  discount: Discount,
  taxRate: number,
  priceIncludesTax: boolean = false,
  exchangeRate: number = 1
): CalculatedTotals => {
  const priceWithQuantity = price * quantity;

  const priceWithoutTax = priceIncludesTax
    ? priceWithQuantity / (1 + taxRate)
    : priceWithQuantity;

  const discountTotal = calculateDiscountTotal(priceWithoutTax, discount) || 0;

  //For Credit Return Sale Item type, price is a negative number
  const priceWithDiscount =
    priceWithoutTax - Math.sign(priceWithoutTax) * discountTotal;

  const taxTotal = roundToDecimals(priceWithDiscount * taxRate, 2);

  const total = priceWithDiscount + taxTotal;

  const multiCurrencyDiscountTotal = toMulticurrencyCalculate(
    discountTotal,
    exchangeRate
  );

  const multiCurrencyTaxTotal = toMulticurrencyCalculate(
    taxTotal,
    exchangeRate
  );

  const multiCurrencyTotal = toMulticurrencyCalculate(total, exchangeRate);

  const multiCurrencySubTotal = toMulticurrencyCalculate(
    priceWithDiscount,
    exchangeRate
  );

  return {
    discountTotal: discountTotal || 0,
    taxTotal,
    total,
    subTotal: priceWithDiscount,
    multiCurrencyDiscountTotal,
    multiCurrencyTaxTotal,
    multiCurrencyTotal,
    multiCurrencySubTotal,
  };
};

export const bundleItemIsCreditReturn = (bundleItem: SalesOrderBundleItem) => {
  return (
    !!bundleItem.salesOrderItems.length &&
    bundleItem.salesOrderItems.every(
      (soi) => soi.salesOrderItemType === SalesOrderItemTypes.CreditReturn
    )
  );
};

export const calculateDiscountTotal = (price: number, discount: Discount) => {
  return discount.type === DiscountTypes.FlatRate
    ? discount.value
    : Math.abs(price) * ((discount.value || 0) / 100);
};

export const calculateTotal = (
  price: number,
  discountTotal: number,
  taxTotal: number
) => {
  return price - discountTotal + taxTotal;
};

export const calculateTaxTotal = (
  taxRate: number,
  price: number,
  discountTotal: number
) => {
  return roundToDecimals((price - discountTotal) * taxRate, 2);
};

export const convertToNegative = (
  value: string,
  rowType: SalesOrderItemTypes | PurchaseOrderItemTypes
) => {
  const negativeRow =
    rowType === SalesOrderItemTypes.CreditReturn ||
    rowType === SalesOrderItemTypes.MiscReturn ||
    rowType === PurchaseOrderItemTypes.CreditReturn ||
    rowType === PurchaseOrderItemTypes.MiscCredit;
  if (negativeRow && parseFloat(value) > 0) {
    return parseFloat('-' + value);
  }
  return parseFloat(value);
};

//calculating prices multiplying or dividing  by factor
//factor can be locaten in item's own uom conversions or in uom's general conversion
//if selected conversion is from then we devide, else we multiply
export const calculatePriceBasedOnUomChange = (
  newUom: Uom,
  oldUom: Uom,
  oldPrice: number,
  itemUomConversions: ItemUomConversion[]
): number => {
  if (!newUom || !oldUom || newUom.id === oldUom.id) {
    return oldPrice;
  }

  const itemUOMConversionFrom = itemUomConversions.find(
    (u) => u.fromUomId === newUom.id && u.toUomId === oldUom.id
  );
  const itemUOMConversionTo = itemUomConversions.find(
    (u) => u.toUomId === newUom.id && u.fromUomId === oldUom.id
  );

  const toActiveUomConversion = newUom.toConversions.find(
    (u) => u.toUomId === oldUom.id
  );
  const fromActiveUomConversion = newUom.fromConversions.find(
    (u) => u.fromUomId === oldUom.id
  );

  const factorDivide = itemUOMConversionTo
    ? _.get(itemUOMConversionTo, 'factor', 1)!
    : fromActiveUomConversion
    ? _.get(fromActiveUomConversion, 'factor', 1)!
    : null;
  const factorMultiply = itemUOMConversionFrom
    ? _.get(itemUOMConversionFrom, 'factor', 1)!
    : toActiveUomConversion
    ? _.get(toActiveUomConversion, 'factor', 1)!
    : null;

  const factor = factorDivide
    ? 1 / factorDivide
    : factorMultiply
    ? factorMultiply
    : 1;

  return oldPrice * factor || 0;
};

export const createDefaultSalesOrderPlaceholder = (
  orderId: number
): SalesOrder => {
  // This function creates a placeholder SalesOrder object.
  // It's used when a full order fetch fails during bulk operations,
  // ensuring the UI can still display basic info about the failed item.
  return {
    id: orderId,
    number: `Failed-${orderId}`,
    status: SalesOrderStatus.Estimate, // Using a non-eligible status as a sensible default
    // --- Nullable Scalar Fields ---
    accountingClassId: null,
    accountingProjectId: null,
    billToCity: null,
    billToCountry: null,
    billToName: null,
    billToState: null,
    billToStreet: null,
    billToStreet2: null,
    billToPostalCode: null,
    billToCompanyName: null,
    billToAttention: null,
    billToPhone: null,
    billToEmail: null,
    carrierId: null,
    carrierServiceId: null,
    channelSource: null,
    customerId: null,
    customer: null, // Assuming Customer type is complex, default to null
    customerPONumber: null,
    dateCreated: new Date(), // Default to now
    dateExpires: null,
    dateFulfilled: null,
    dateIssued: null,
    dateLastModified: new Date(), // Default to now
    dateScheduled: null,
    dateDue: null,
    emails: null,
    emailStatus: null,
    exchangeCurrency: null, // Assuming Currency type is complex, default to null
    exchangeCurrencyId: null,
    exchangeRate: null,
    fobPointId: null,
    grandTotal: 0, // Default numeric to 0 or null based on type, SalesOrder has it as number | null
    homeCurrency: null, // Assuming Currency type is complex, default to null
    homeCurrencyId: null,
    locationId: null,
    location: null, // Assuming Location type is complex, default to null
    multiCurrencyDiscountTotal: null,
    multiCurrencyGrandTotal: null,
    multiCurrencyOrderLevelDiscountTotal: null,
    multiCurrencyPaymentTotal: null,
    multiCurrencySubtotal: null,
    multiCurrencyTaxTotal: null,
    margin: null,
    marginPercent: null,
    markupPercent: null,
    markup: null,
    notes: null,
    orderLevelDiscountFlatAmount: null,
    orderLevelDiscountPercent: null,
    paymentTerm: null, // Assuming PaymentTerm type is complex, default to null
    paymentTermId: null,
    paymentTotal: 0, // Default numeric to 0 or null
    payments: [], // Default array to empty
    picks: [], // Default array to empty
    phone: null,
    printStatus: null,
    priorityId: null,
    representativeId: null,
    representative: null, // Assuming Representative type is complex, default to null
    salesTax: null, // Assuming TaxRate type is complex, default to null
    salesTaxId: null,
    shippingTermId: null,
    shipToCity: null,
    shipToCountry: null,
    shipToName: null,
    shipToState: null,
    shipToStreet: null,
    shipToStreet2: null,
    shipToPostalCode: null,
    shipToCompanyName: null,
    shipToAttention: null,
    shipToPhone: null,
    shipToEmail: null,
    stockStatus: null, // Explicitly null for placeholder
    store: null,
    receipt: null,
    vendorPONumber: null,
    version: null,
    // --- Boolean Fields ---
    billToResidential: false,
    billToVerified: false,
    deleted: false,
    shipToResidential: false,
    shipToVerified: false,
    // --- Array Fields (already defaulted above where appropriate) ---
    customFields: [],
    documents: [],
    dropShipPurchaseOrders: [],
    salesOrderItemList: [],
    shipments: [],
    salesOrderBundleItems: [],
    salesItems: [], // This is a union type, default to empty array
  };
};

export const resolveDiscount = (
  discountFlatAmount: number | null | undefined,
  discountPercent: number | null | undefined
): Discount => ({
  value: discountPercent
    ? discountPercent * 100
    : discountFlatAmount
    ? discountFlatAmount
    : 0,
  type: discountPercent ? DiscountTypes.Percent : DiscountTypes.FlatRate,
});
